import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ContentCopy from "@mui/icons-material/ContentCopy";
import { Box, DialogContentText, IconButton, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { type DateTime, Interval } from "luxon";
import { type FC, useState } from "react";
import { useTranslation } from "react-i18next";
import SystemDialog from "../../components/UIToolkit/Popups/Dialog";
import SystemButton from "../../components/UIToolkit/SystemButton/SystemButton";
import { useHospitalData } from "../../contexts/HospitalDataProvider";
import { INK, NEUTRAL, STATUS } from "../../theme/tokens";
import { EmploymentType, type SelfScheduleCalendarDaysData, SelfSchedulingPreference } from "../../utils/models";
import { SelfSchedulingDay } from "./SelfScheduleDay/SelfSchedulingDay";
import { useSelfSchedulingCalendarState } from "./state/useSelfSchedulingCalendarState";

type SelfSchedulingCalendarProps = {
	onDayClick?: (date: DateTime) => void;
	selectedDate?: DateTime | null;
	daysData: SelfScheduleCalendarDaysData;
	cellWidth?: string;
	cellHeight?: string;
	cellWidthXS?: string;
	cellHeightXS?: string;
	borderColor?: string;
	targetWorkingDaysPerWeek?: number;
	overtimeWarningColorSecondary?: string;
	navigateToPreviousPeriod?: () => void;
	navigateToNextPeriod?: () => void;
	schedulingPeriod?: { periodStart: DateTime; periodEnd: DateTime };
	isViewingPastPeriod?: boolean;
	isViewingLockedPeriod?: boolean;
	isViewingCurrentPeriod?: boolean;
	isInitializing?: boolean;
	isPublishing?: boolean;
	copyLastMonthPreferences: () => void;
};

const SelfSchedulingCalendar: FC<SelfSchedulingCalendarProps> = ({
	daysData,
	onDayClick,
	selectedDate,
	cellWidth = "65px",
	cellHeight = "85px",
	borderColor = NEUTRAL[150],
	targetWorkingDaysPerWeek = 3,
	overtimeWarningColorSecondary = alpha(STATUS.ERROR.main, 0.1),
	cellWidthXS = "50px",
	cellHeightXS = "75px",
	navigateToPreviousPeriod,
	navigateToNextPeriod,
	schedulingPeriod,
	isViewingPastPeriod,
	isViewingLockedPeriod,
	isInitializing,
	isPublishing,
	copyLastMonthPreferences,
}) => {
	const { t } = useTranslation();
	const { activeBedsideNurse } = useHospitalData();
	const [showCopyConfirmDialog, setShowCopyConfirmDialog] = useState<boolean>(false);

	const handleCopyConfirm = (): void => {
		copyLastMonthPreferences();
		setShowCopyConfirmDialog(false);
	};

	const {
		firstDate,
		calendarLabel,
		handleDayClick,
		amountOfDaysInWeek,
		firstDayOfWeeksWithOvertimeWarning,
		firstDayOfWeeksWithUnderThresholdWarning,
		calculatedMaxWeekCount,
	} = useSelfSchedulingCalendarState({
		targetWorkingDaysPerWeek,
		daysData,
		onDayClick,
		schedulingPeriod,
	});

	return (
		<Box
			data-testid="self-scheduling-calendar"
			sx={{
				display: "flex",
				flexDirection: "column",
				alignItems: "center",
				justifyContent: "start",
				width: "100%",
				height: "fit-content",
			}}
		>
			<SystemDialog
				title={t("confirm.action")}
				body={<DialogContentText>{t("copy.last.month.preferences.confirm")}</DialogContentText>}
				open={showCopyConfirmDialog}
				onClose={() => setShowCopyConfirmDialog(false)}
				onConfirm={handleCopyConfirm}
			/>
			<Box
				sx={{
					display: "flex",
					alignItems: "center",
					gap: 2,
				}}
			>
				<IconButton onClick={navigateToPreviousPeriod} size="small" disabled={!navigateToPreviousPeriod}>
					<ChevronLeftIcon />
				</IconButton>
				<Typography
					sx={{ fontWeight: 600, fontSize: "16px", lineHeight: "20px", userSelect: "none", color: INK.SECONDARY, minWidth: "150px", textAlign: "center" }}
				>
					{calendarLabel}
				</Typography>
				<IconButton onClick={navigateToNextPeriod} size="small" disabled={!navigateToNextPeriod}>
					<ChevronRightIcon />
				</IconButton>
				{!isViewingPastPeriod && (
					<SystemButton
						onClick={() => setShowCopyConfirmDialog(true)}
						disabled={isViewingLockedPeriod || isInitializing || isPublishing}
						buttonVariant="secondary"
						leadingIcon={<ContentCopy sx={{ fontSize: "18px" }} />}
						size="small"
						sx={{ border: "none", p: 0, width: "fit-content", minWidth: "fit-content", ml: "auto" }}
					/>
				)}
			</Box>
			<Box
				sx={{
					display: "flex",
					flexDirection: "column",
					width: "100%",
					height: "fit-content",
					justifyContent: "center",
					alignItems: "center",
					mt: 2,
				}}
			>
				<Box
					sx={{
						display: "flex",
						flexDirection: "row",
						width: "fit-content",
						justifyContent: "center",
					}}
				>
					{Array.from({ length: amountOfDaysInWeek }, (_, i) => {
						const currentDate = firstDate.plus({ day: i });
						return (
							<Typography
								key={currentDate.toISO()}
								align={"center"}
								sx={{
									fontWeight: 600,
									fontSize: "12px",
									lineHeight: "100%",
									color: alpha(INK.SECONDARY, 0.5),
									userSelect: "none",
									width: { xs: cellWidthXS, mediumPhone: cellWidth },
								}}
							>
								{currentDate.toFormat("ccc")}
							</Typography>
						);
					})}
				</Box>
				<Box sx={{ display: "flex", flexDirection: "row", alignItems: "center", justifyContent: "center", width: "fit-content", mt: 1 }}>
					{Array.from({ length: amountOfDaysInWeek }, (_, dayIndex) => {
						const daysInWeek = firstDate.plus({ days: dayIndex });
						return (
							<Box
								key={daysInWeek.toISO()}
								sx={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", width: "fit-content" }}
							>
								{Array.from({ length: calculatedMaxWeekCount }, (_, weekIndex) => {
									const currentDate = daysInWeek.plus({ weeks: weekIndex });

									const formattedDate = currentDate.toFormat("yyyy-MM-dd");
									const specificDateData = daysData[formattedDate] ?? {
										dayPreference: SelfSchedulingPreference.UNDEFINED,
										date: currentDate,
									};

									const markOvertimeWeek =
										activeBedsideNurse?.employmentType !== EmploymentType.PartTime &&
										/*We check if the current date is related to any overtime warning week - if so we should mark it!*/
										firstDayOfWeeksWithOvertimeWarning.some((firstDayOfWeekOfWarning) => {
											// Check for null/undefined before accessing properties
											if (!firstDayOfWeekOfWarning) return false;

											const lastDayOfWeekOfWarning = firstDayOfWeekOfWarning.plus({ days: 6 }).endOf("day");

											// Luxon's Interval.fromDateTimes can be used for "isBetween" functionality
											const weekInterval = Interval.fromDateTimes(firstDayOfWeekOfWarning, lastDayOfWeekOfWarning);
											return weekInterval.contains(currentDate);
										});

									const markUnderThresholdWeek =
										/*We check if the current date is related to any under-threshold warning week - if so we should mark it!*/
										firstDayOfWeeksWithUnderThresholdWarning.some((firstDayOfWeekOfWarning) => {
											// Check for null/undefined before accessing properties
											if (!firstDayOfWeekOfWarning) return false;

											const lastDayOfWeekOfWarning = firstDayOfWeekOfWarning.plus({ days: 6 }).endOf("day");

											// Luxon's Interval.fromDateTimes can be used for "isBetween" functionality
											const weekInterval = Interval.fromDateTimes(firstDayOfWeekOfWarning, lastDayOfWeekOfWarning);
											return weekInterval.contains(currentDate);
										});

									// Determine background color: overtime takes priority over under-threshold
									const bgColor =
										activeBedsideNurse?.employmentType === EmploymentType.PRN
											? "transparent"
											: markOvertimeWeek
												? overtimeWarningColorSecondary
												: markUnderThresholdWeek
													? STATUS.ERROR.light
													: "transparent";

									return (
										<Box
											key={formattedDate}
											style={{
												display: "flex",
												alignItems: "center",
												justifyContent: "center",
												borderLeft: `1px solid ${borderColor}`,
												borderTop: `1px solid ${borderColor}`,
												borderBottom: `1px solid ${weekIndex === calculatedMaxWeekCount - 1 ? borderColor : "transparent"}`,
												borderRight: `1px solid ${dayIndex === amountOfDaysInWeek - 1 ? borderColor : "transparent"}`,
												background: bgColor,
											}}
											sx={{ width: { xs: cellWidthXS, mediumPhone: cellWidth }, height: { xs: cellHeightXS, mediumPhone: cellHeight } }}
										>
											<SelfSchedulingDay
												key={`${formattedDate}-${specificDateData.dayPreference}`}
												dayData={specificDateData}
												onClick={handleDayClick}
												isSelected={selectedDate?.toISO() === specificDateData.date.toISO()}
											/>
										</Box>
									);
								})}
							</Box>
						);
					})}
				</Box>
			</Box>
		</Box>
	);
};

export default SelfSchedulingCalendar;
